// src/components/tiptap/extensions/InteractiveVideoNode.jsx
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { Node, mergeAttributes } from "@tiptap/core";
import { ReactNodeViewRenderer, NodeViewWrapper } from "@tiptap/react";
import { Plugin, NodeSelection } from "prosemirror-state";
import { supabase } from "../../../config/supabase";
import * as FiIcons from "react-icons/fi";
import SafeIcon from "../../../common/SafeIcon";
import ConfirmModal from "../../common/ConfirmModal";

const { FiVideo, FiPlus, FiSave, FiX, FiMail, FiUsers, FiSend, FiTrash2, FiEdit2, FiMic, FiSquare, FiPlay, FiRotateCcw, FiCamera } = FiIcons;

// NOTE:
// We intentionally avoid heavy stopPropagation/stopEvent hacks.
// The node is made non-selectable + we block Backspace/Delete near it,
// which preserves tool integrity without breaking React clicks.

function LiveStreamVideo({ stream, className }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    try {
      el.srcObject = stream || null;
    } catch {}
    return () => {
      try {
        if (ref.current) ref.current.srcObject = null;
      } catch {}
    };
  }, [stream]);
  if (!stream) return null;
  return (
    <video
      ref={ref}
      className={className}
      autoPlay
      muted
      playsInline
    />
  );
}

function toast(msg) {
  try {
    let el = document.getElementById("mms-toast");
    if (!el) {
      el = document.createElement("div");
      el.id = "mms-toast";
      Object.assign(el.style, {
        position: "fixed",
        left: "50%",
        bottom: "28px",
        transform: "translateX(-50%)",
        padding: "10px 14px",
        borderRadius: "10px",
        boxShadow: "0 6px 20px rgba(0,0,0,.18)",
        fontSize: "14px",
        fontWeight: "600",
        color: "#201919",
        background: "#fff",
        border: "1px solid #e5e7eb",
        opacity: "0",
        zIndex: 10000,
        pointerEvents: "none",
        transition: "opacity 160ms ease",
      });
      document.body.appendChild(el);
    }
    el.textContent = msg;
    requestAnimationFrame(() => { el.style.opacity = "1"; });
    clearTimeout(el._t);
    el._t = setTimeout(() => { el.style.opacity = "0"; }, 1600);
  } catch {}
}

/* -------------------------------------------------------------------------- */
/* Audio recording helpers (MediaRecorder with Safari fallback)                */
/* -------------------------------------------------------------------------- */
function pickAudioMimeType() {
  const candidates = [
    "audio/webm;codecs=opus",
    "audio/webm",
    "audio/mp4",
    "audio/aac",
    "audio/mpeg",
  ];
  try {
    if (typeof window === "undefined" || typeof MediaRecorder === "undefined") return null;
    for (const t of candidates) {
      if (t && MediaRecorder.isTypeSupported?.(t)) return t;
    }
    return "";
  } catch {
    return "";
  }
}

async function getBlobDurationMs(blob) {
  try {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("audio");
    a.preload = "metadata";
    a.src = url;
    const ms = await new Promise((resolve, reject) => {
      const onLoaded = () => {
        const d = a.duration;
        cleanup();
        if (!Number.isFinite(d) || d <= 0) return resolve(null);
        resolve(Math.floor(d * 1000));
      };
      const onErr = () => {
        cleanup();
        resolve(null);
      };
      const cleanup = () => {
        a.removeEventListener("loadedmetadata", onLoaded);
        a.removeEventListener("error", onErr);
        URL.revokeObjectURL(url);
      };
      a.addEventListener("loadedmetadata", onLoaded);
      a.addEventListener("error", onErr);
    });
    return ms;
  } catch {
    return null;
  }
}

function pickVideoMimeType() {
  const candidates = [
    "video/webm;codecs=vp9,opus",
    "video/webm;codecs=vp8,opus",
    "video/webm",
    "video/mp4",
  ];
  try {
    if (typeof window === "undefined" || typeof MediaRecorder === "undefined") return null;
    for (const t of candidates) {
      if (t && MediaRecorder.isTypeSupported?.(t)) return t;
    }
    return "";
  } catch {
    return "";
  }
}

async function getVideoBlobDurationMs(blob) {
  try {
    const url = URL.createObjectURL(blob);
    const v = document.createElement("video");
    v.preload = "metadata";
    v.src = url;
    const ms = await new Promise((resolve) => {
      const onLoaded = () => {
        const d = v.duration;
        cleanup();
        if (!Number.isFinite(d) || d <= 0) return resolve(null);
        resolve(Math.floor(d * 1000));
      };
      const onErr = () => { cleanup(); resolve(null); };
      const cleanup = () => {
        v.removeEventListener("loadedmetadata", onLoaded);
        v.removeEventListener("error", onErr);
        URL.revokeObjectURL(url);
      };
      v.addEventListener("loadedmetadata", onLoaded);
      v.addEventListener("error", onErr);
    });
    return ms;
  } catch {
    return null;
  }
}

function fileExtFromMime(mime) {
  const m = String(mime || "").toLowerCase();
  if (m.includes("video/mp4")) return "mp4";
  if (m.includes("video/webm")) return "webm";
  if (m.includes("webm")) return "webm";
  if (m.includes("mp4") || m.includes("aac")) return "m4a";
  if (m.includes("mpeg")) return "mp3";
  if (m.includes("ogg")) return "ogg";
  if (m.includes("wav")) return "wav";
  return "m4a";
}

async function makeVideoPosterDataUrl(url, { seekSeconds = 0.8 } = {}) {
  // Best-effort: if CORS blocks canvas, we just fail silently.
  return await new Promise((resolve) => {
    try {
      const v = document.createElement("video");
      v.preload = "metadata";
      v.muted = true;
      v.playsInline = true;
      v.crossOrigin = "anonymous";
      v.src = url;

      const done = (val) => {
        try {
          v.pause?.();
          v.removeAttribute("src");
          v.load?.();
        } catch {}
        resolve(val || null);
      };

      v.addEventListener(
        "error",
        () => {
          done(null);
        },
        { once: true }
      );

      v.addEventListener(
        "loadedmetadata",
        () => {
          try {
            const dur = Number.isFinite(v.duration) ? v.duration : 0;
            const t = Math.max(0, Math.min(dur > 0 ? Math.min(seekSeconds, dur * 0.25) : seekSeconds, dur || seekSeconds));
            // Some browsers need a tiny delay before seeking is honored.
            setTimeout(() => {
              try {
                v.currentTime = t;
              } catch {
                done(null);
              }
            }, 50);
          } catch {
            done(null);
          }
        },
        { once: true }
      );

      v.addEventListener(
        "seeked",
        () => {
          try {
            const w = v.videoWidth || 0;
            const h = v.videoHeight || 0;
            if (!w || !h) return done(null);
            const c = document.createElement("canvas");
            c.width = w;
            c.height = h;
            const ctx = c.getContext("2d");
            if (!ctx) return done(null);
            ctx.drawImage(v, 0, 0, w, h);
            done(c.toDataURL("image/jpeg", 0.72));
          } catch {
            done(null);
          }
        },
        { once: true }
      );
    } catch {
      resolve(null);
    }
  });
}

async function deleteInteractiveVideoCascade({ instanceId, lessonNoteId, noteBlockId }) {
  let iid = instanceId;
  if (!iid && lessonNoteId && noteBlockId) {
    const r = await supabase
      .from("interactive_video_instances")
      .select("id")
      .eq("lesson_note_id", lessonNoteId)
      .eq("note_block_id", noteBlockId)
      .maybeSingle();
    iid = r?.data?.id || null;
  }
  if (!iid) return;

  await supabase.from("interactive_video_comments").delete().eq("instance_id", iid);
  await supabase.from("interactive_video_videos").delete().eq("instance_id", iid);
  try {
    await supabase.from("interactive_video_upload_tokens").delete().eq("instance_id", iid);
  } catch (e) {}
  await supabase.from("interactive_video_instances").delete().eq("id", iid);
}

const AUDIENCE_OPTIONS = [
  { value: "both", label: "Parents & students" },
  { value: "parents", label: "Parents only" },
  { value: "students", label: "Students only" },
];

function InteractiveVideoBlock({ node, updateAttributes, editor, getPos }) {
  const attrs = node.attrs || {};
  const ctx = editor?.storage?.mms ?? {};
  const isEditable = editor?.isEditable;

  const videoRef = useRef(null);
  const commentsPaneRef = useRef(null);
  const commentElByIdRef = useRef(new Map());

  const [instanceId, setInstanceId] = useState(attrs.instanceId ?? null);
  const [video, setVideo] = useState(null);
  const [comments, setComments] = useState([]);
  const [saving, setSaving] = useState(false);
  const [confirm, setConfirm] = useState(null);

  // playback + timeline
  const [durationSec, setDurationSec] = useState(0);
  const [currentTimeSec, setCurrentTimeSec] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [activeCommentId, setActiveCommentId] = useState(null);
  const [flashCommentId, setFlashCommentId] = useState(null);
  const [flashMarkerId, setFlashMarkerId] = useState(null);

  // inline-draft / editing
  const [draftById, setDraftById] = useState({});
  const [editingId, setEditingId] = useState(null);

  // Audio drafts + playback URLs
  const [draftTypeById, setDraftTypeById] = useState({});
  const [draftRealIdByTmpId, setDraftRealIdByTmpId] = useState({});
  const [audioPreviewUrlById, setAudioPreviewUrlById] = useState({});
  const [audioBlobById, setAudioBlobById] = useState({});
  const [audioMimeById, setAudioMimeById] = useState({});
  const [audioDurationMsById, setAudioDurationMsById] = useState({});
  const recorderRefByTmpId = useRef(new Map());
  const chunksRefByTmpId = useRef(new Map());
  const streamRefByTmpId = useRef(new Map());
  const [isRecordingByTmpId, setIsRecordingByTmpId] = useState({});
  const [signedMediaUrlByCommentId, setSignedMediaUrlByCommentId] = useState({});
  const [videoPosterByCommentId, setVideoPosterByCommentId] = useState({});

  // Video drafts + preview
  const [liveVideoStreamByTmpId, setLiveVideoStreamByTmpId] = useState({});
  const [videoPreviewUrlById, setVideoPreviewUrlById] = useState({});
  const [videoBlobById, setVideoBlobById] = useState({});
  const [videoMimeById, setVideoMimeById] = useState({});
  const [videoDurationMsById, setVideoDurationMsById] = useState({});
  const videoRecorderRefByTmpId = useRef(new Map());
  const videoChunksRefByTmpId = useRef(new Map());
  const videoStreamRefByTmpId = useRef(new Map());
  const [isVideoRecordingByTmpId, setIsVideoRecordingByTmpId] = useState({});

  const canDelete = !!ctx?.userId && !!isEditable;

  // Invite modal
  const [inviteOpen, setInviteOpen] = useState(false);
  const [audience, setAudience] = useState("both");
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [sendingInvite, setSendingInvite] = useState(false);
  const [inviteError, setInviteError] = useState("");
  const [recipients, setRecipients] = useState([]);
  const [templateId, setTemplateId] = useState(null);
  const [missingStudents, setMissingStudents] = useState([]);
  const [missingParents, setMissingParents] = useState([]);
  const [sentCount, setSentCount] = useState(null);
  const [ccTeacher, setCcTeacher] = useState(false);
  const [notifyOnUpload, setNotifyOnUpload] = useState(true);

  const teacherEmail = editor?.storage?.mms?.teacherEmail || "";

  useEffect(() => {
    if (!attrs.noteBlockId) {
      updateAttributes({
        noteBlockId: crypto.randomUUID(),
        lessonNoteId: ctx.lessonNoteId ?? null,
        studentId: ctx.studentId ?? null,
        studentMasterId: ctx.masterStudentId ?? null,
        schoolId: ctx.schoolId ?? null,
      });
    }
  }, []);

  useEffect(() => {
    if (!attrs.noteBlockId) return;

    (async () => {
      const { data, error } = await supabase.rpc(
        "get_or_create_interactive_video_instance",
        {
          p_note_block_id: attrs.note_block_id || attrs.noteBlockId,
          p_school_id: ctx.schoolId,
          p_lesson_note_id: ctx.lessonNoteId,
          p_student_id: ctx.studentId,
          p_student_master_id: ctx.masterStudentId,
          p_created_by_user_id: ctx.userId,
        }
      );

      if (error) return console.error(error);

      setInstanceId(data.id);
      updateAttributes({ instanceId: data.id });

      const { data: vids } = await supabase
        .from("interactive_video_videos")
        .select("*")
        .eq("instance_id", data.id)
        .order("uploaded_at", { ascending: false })
        .limit(1)
        .maybeSingle();

      setVideo(vids ?? null);

      const { data: rows } = await supabase
        .from("interactive_video_comments")
        .select("*")
        .eq("instance_id", data.id)
        .eq("is_hidden", false)
        .order("time_ms", { ascending: true });

      setComments(rows ?? []);
    })();
  }, [attrs.noteBlockId]);

  /* ─────────────────────────────
     Sign media URLs for audio/video comments
  ───────────────────────────── */
  useEffect(() => {
    let cancelled = false;
    const toSign = (comments || []).filter(
      (c) =>
        c &&
        (c.comment_type === "audio" || c.comment_type === "video") &&
        c.media_key &&
        !signedMediaUrlByCommentId?.[c.id]
    );
    if (toSign.length === 0) return;

    (async () => {
      for (const c of toSign) {
        try {
          const { data, error } = await supabase.functions.invoke(
            "interactive-video-comment-media-sign",
            { body: { commentId: c.id } }
          );
          if (cancelled) return;
          if (!error && data?.signedUrl) {
            setSignedMediaUrlByCommentId((m) => ({ ...m, [c.id]: data.signedUrl }));
          }
        } catch {}
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [comments]);

  // Generate better thumbnails for video comments (avoid black 0:00 frame)
  useEffect(() => {
    let cancelled = false;
    const vids = (comments || []).filter(
      (c) =>
        c &&
        c.comment_type === "video" &&
        (signedMediaUrlByCommentId?.[c.id] || "") &&
        !videoPosterByCommentId?.[c.id]
    );
    if (vids.length === 0) return;

    (async () => {
      for (const c of vids) {
        if (cancelled) return;
        const url = signedMediaUrlByCommentId?.[c.id];
        if (!url) continue;
        const poster = await makeVideoPosterDataUrl(url, { seekSeconds: 0.8 });
        if (cancelled) return;
        if (poster) {
          setVideoPosterByCommentId((m) => ({ ...m, [c.id]: poster }));
        } else {
          // Avoid re-trying forever on CORS-restricted URLs
          setVideoPosterByCommentId((m) => ({ ...m, [c.id]: "" }));
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [comments, signedMediaUrlByCommentId, videoPosterByCommentId]);

  useEffect(() => {
    if (inviteOpen) return;
    setLoadingPreview(false);
    setSendingInvite(false);
    setInviteError("");
    setRecipients([]);
    setTemplateId(null);
    setMissingStudents([]);
    setMissingParents([]);
    setSentCount(null);
    setCcTeacher(false);
    setNotifyOnUpload(true);
  }, [inviteOpen]);

  useEffect(() => {
    if (!inviteOpen || !instanceId || !isEditable) return;

    let cancelled = false;
    (async () => {
      setLoadingPreview(true);
      setInviteError("");
      setSentCount(null);

      try {
        const { data, error } = await supabase.functions.invoke(
          "interactive-video-invite-send",
          { body: { instanceId, audience: audience || "both" } }
        );
        if (cancelled) return;

        if (error) {
          setInviteError(error.message || "Failed to load recipients.");
          return;
        }

        const recs = Array.isArray(data?.recipients) ? data.recipients : [];
        setTemplateId(data?.templateId ?? data?.previewTemplateId ?? null);
        setRecipients(
          recs.map((r, idx) => ({
            id: `${r.studentMasterId || "na"}-${r.type}-${r.email}-${idx}`,
            email: r.email,
            name: r.name,
            type: r.type,
            studentMasterId: r.studentMasterId,
            selected: true,
          }))
        );
        setMissingStudents(Array.isArray(data?.missingStudentEmails) ? data.missingStudentEmails : []);
        setMissingParents(Array.isArray(data?.missingParentEmails) ? data.missingParentEmails : []);
      } catch (err) {
        if (cancelled) return;
        setInviteError("Failed to load recipients.");
      } finally {
        if (!cancelled) setLoadingPreview(false);
      }
    })();
    return () => { cancelled = true; };
  }, [inviteOpen, instanceId, isEditable, audience]);

  const toggleRecipient = (id) => {
    setRecipients((prev) => prev.map((r) => (r.id === id ? { ...r, selected: !r.selected } : r)));
  };

  const handleSendInvite = async () => {
    setInviteError("");
    const selected = recipients.filter((r) => r.selected);
    if (selected.length === 0) {
      setInviteError("Please select at least one recipient.");
      return;
    }

    setSendingInvite(true);
    try {
      const { data, error } = await supabase.functions.invoke(
        "interactive-video-invite-send",
        {
          body: {
            instanceId,
            audience: audience || "both",
            ccTeacher: !!ccTeacher,
            notifyOnUpload: !!notifyOnUpload,
            recipients: selected.map(({ email, name, type, studentMasterId }) => ({
              email,
              name,
              type,
              studentMasterId,
            })),
          },
        }
      );

      if (error) {
        setInviteError(error.message || "Failed to send emails.");
        return;
      }

      const sentCountNum = Array.isArray(data?.sent) ? data.sent.length : (typeof data?.sent === "number" ? data.sent : (typeof data?.count === "number" ? data.count : selected.length));
      setSentCount(sentCountNum);
    } catch (err) {
      setInviteError("Failed to send emails.");
    } finally {
      setSendingInvite(false);
    }
  };

  const genTempId = () => `tmp_${crypto.randomUUID()}`;

  const markerColors = useMemo(() => [
    { chip: "bg-pink-100 text-pink-700 border-pink-200", ring: "ring-pink-300", dot: "bg-pink-400" },
    { chip: "bg-sky-100 text-sky-700 border-sky-200", ring: "ring-sky-300", dot: "bg-sky-400" },
    { chip: "bg-amber-100 text-amber-800 border-amber-200", ring: "ring-amber-300", dot: "bg-amber-400" },
    { chip: "bg-emerald-100 text-emerald-800 border-emerald-200", ring: "ring-emerald-300", dot: "bg-emerald-400" },
    { chip: "bg-violet-100 text-violet-700 border-violet-200", ring: "ring-violet-300", dot: "bg-violet-400" },
    { chip: "bg-orange-100 text-orange-800 border-orange-200", ring: "ring-orange-300", dot: "bg-orange-400" },
  ], []);

  const colorFor = useCallback((comment, idx) => {
    const seed = typeof comment?.time_ms === "number" ? comment.time_ms : idx;
    const i = Math.abs(seed) % markerColors.length;
    return markerColors[i];
  }, [markerColors]);

  const scrollToComment = useCallback((commentId) => {
    const el = commentElByIdRef.current.get(commentId);
    if (!el) return;
    try {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
    } catch {}
    setFlashCommentId(commentId);
    window.clearTimeout(scrollToComment._t);
    scrollToComment._t = window.setTimeout(() => setFlashCommentId(null), 700);
  }, []);

  const pulseMarker = useCallback((commentId) => {
    setFlashMarkerId(commentId);
    window.clearTimeout(pulseMarker._t);
    pulseMarker._t = window.setTimeout(() => setFlashMarkerId(null), 700);
  }, []);

  const seekTo = useCallback((ms, { resume } = {}) => {
    const el = videoRef.current;
    if (!el) return;
    const shouldResume = typeof resume === "boolean" ? resume : isPlaying;
    el.currentTime = ms / 1000;
    if (shouldResume) el.play?.();
    else el.pause?.();
  }, [isPlaying]);

  const handleMarkerClick = useCallback((c) => {
    setActiveCommentId(c.id);
    scrollToComment(c.id);
    seekTo(c.time_ms, { resume: isPlaying });
  }, [scrollToComment, seekTo, isPlaying]);

  const handleCardClick = useCallback((c) => {
    setActiveCommentId(c.id);
    seekTo(c.time_ms, { resume: isPlaying });
    pulseMarker(c.id);
  }, [seekTo, pulseMarker, isPlaying]);

  const addDraftCommentAtCurrentTime = useCallback(() => {
    if (!videoRef.current || !instanceId) return;
    const timeMs = Math.floor(videoRef.current.currentTime * 1000);
    const tmpId = genTempId();

    const draftRow = { id: tmpId, instance_id: instanceId, time_ms: timeMs, body: "", isDraft: true };
    setComments((prev) => [...prev, draftRow].sort((a, b) => (a.time_ms ?? 0) - (b.time_ms ?? 0)));
    setDraftById((d) => ({ ...d, [tmpId]: "" }));
    setDraftTypeById((t) => ({ ...t, [tmpId]: "text" }));
    setDraftRealIdByTmpId((m) => ({ ...m, [tmpId]: crypto.randomUUID() }));
    setActiveCommentId(tmpId);

    requestAnimationFrame(() => {
      scrollToComment(tmpId);
      requestAnimationFrame(() => {
        const el = commentElByIdRef.current.get(tmpId);
        const ta = el?.querySelector?.("textarea");
        if (ta) ta.focus();
      });
    });
  }, [instanceId, scrollToComment]);

  const cleanupDraftAudio = useCallback((tmpId) => {
    try { recorderRefByTmpId.current.get(tmpId)?.stop?.(); } catch {}
    recorderRefByTmpId.current.delete(tmpId);
    try {
      const st = streamRefByTmpId.current.get(tmpId);
      st?.getTracks?.()?.forEach((t) => t.stop());
    } catch {}
    streamRefByTmpId.current.delete(tmpId);
    chunksRefByTmpId.current.delete(tmpId);

    const url = audioPreviewUrlById?.[tmpId];
    if (url) {
      try { URL.revokeObjectURL(url); } catch {}
    }
    setAudioPreviewUrlById((m) => {
      const next = { ...m };
      delete next[tmpId];
      return next;
    });
    setAudioBlobById((m) => {
      const next = { ...m };
      delete next[tmpId];
      return next;
    });
    setAudioMimeById((m) => {
      const next = { ...m };
      delete next[tmpId];
      return next;
    });
    setAudioDurationMsById((m) => {
      const next = { ...m };
      delete next[tmpId];
      return next;
    });
    setIsRecordingByTmpId((m) => ({ ...m, [tmpId]: false }));
  }, [audioPreviewUrlById]);

  const cleanupDraftVideo = useCallback((tmpId) => {
    try { videoRecorderRefByTmpId.current.get(tmpId)?.stop?.(); } catch {}
    videoRecorderRefByTmpId.current.delete(tmpId);
    try {
      const st = videoStreamRefByTmpId.current.get(tmpId);
      st?.getTracks?.()?.forEach((t) => t.stop());
    } catch {}
    videoStreamRefByTmpId.current.delete(tmpId);
    videoChunksRefByTmpId.current.delete(tmpId);

    setLiveVideoStreamByTmpId((m) => {
      const next = { ...m }; delete next[tmpId]; return next;
    });

    const url = videoPreviewUrlById?.[tmpId];
    if (url) {
      try { URL.revokeObjectURL(url); } catch {}
    }
    setVideoPreviewUrlById((m) => { const next = { ...m }; delete next[tmpId]; return next; });
    setVideoBlobById((m) => { const next = { ...m }; delete next[tmpId]; return next; });
    setVideoMimeById((m) => { const next = { ...m }; delete next[tmpId]; return next; });
    setVideoDurationMsById((m) => { const next = { ...m }; delete next[tmpId]; return next; });
    setIsVideoRecordingByTmpId((m) => ({ ...m, [tmpId]: false }));
  }, [videoPreviewUrlById]);

  const saveDraftComment = useCallback(async (tmpId) => {
    const row = comments.find((c) => c.id === tmpId);
    if (!row) return;
    const draftType = draftTypeById?.[tmpId] ?? "text";

    if (draftType === "text") {
      const body = (draftById?.[tmpId] ?? "").trim();
      if (!body) {
        setComments((prev) => prev.filter((c) => c.id !== tmpId));
        setDraftById((d) => { const next = { ...d }; delete next[tmpId]; return next; });
        setDraftTypeById((t) => { const next = { ...t }; delete next[tmpId]; return next; });
        setDraftRealIdByTmpId((m) => { const next = { ...m }; delete next[tmpId]; return next; });
        cleanupDraftAudio(tmpId);
        cleanupDraftVideo(tmpId);
        return;
      }

      setSaving(true);
      const { data, error } = await supabase
        .from("interactive_video_comments")
        .insert({ instance_id: instanceId, time_ms: row.time_ms, body, comment_type: "text" })
        .select().single();
      setSaving(false);

      if (error) return alert(error.message);

      setComments((prev) => prev.map((c) => (c.id === tmpId ? data : c)).sort((a, b) => (a.time_ms ?? 0) - (b.time_ms ?? 0)));
      setDraftById((d) => { const next = { ...d }; delete next[tmpId]; return next; });
      setDraftTypeById((t) => { const next = { ...t }; delete next[tmpId]; return next; });
      setDraftRealIdByTmpId((m) => { const next = { ...m }; delete next[tmpId]; return next; });
      cleanupDraftAudio(tmpId);
      cleanupDraftVideo(tmpId);
      setActiveCommentId(data.id);
      scrollToComment(data.id);
      pulseMarker(data.id);
      return;
    }

    const isAudio = draftType === "audio";
    const isVideo = draftType === "video";
    const blob = isAudio ? (audioBlobById?.[tmpId] ?? null) : (videoBlobById?.[tmpId] ?? null);
    const mimeType = isAudio ? (audioMimeById?.[tmpId] ?? null) : (videoMimeById?.[tmpId] ?? null);
    const durationMs = isAudio ? (audioDurationMsById?.[tmpId] ?? null) : (videoDurationMsById?.[tmpId] ?? null);
    const realId = draftRealIdByTmpId?.[tmpId] ?? null;

    if (!blob || !realId) {
      toast(isVideo ? "Record a video note first." : "Record a voice note first.");
      return;
    }

    setSaving(true);
    try {
      const form = new FormData();
      const ext = fileExtFromMime(mimeType);
      form.append("file", new File([blob], `${realId}.${ext}`, { type: mimeType || "application/octet-stream" }));
      form.append("instanceId", instanceId);
      form.append("commentId", realId);
      form.append("timeMs", String(row.time_ms ?? 0));
      form.append("commentType", isVideo ? "video" : "audio");
      if (durationMs != null) form.append("durationMs", String(durationMs));
      if (mimeType) form.append("mimeType", String(mimeType));

      const session = (await supabase.auth.getSession()).data.session;
      if (!session) {
        setSaving(false);
        alert("Not authenticated");
        return;
      }

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

      if (!supabaseUrl || !supabaseAnonKey) {
        setSaving(false);
        alert("Missing Supabase env vars");
        return;
      }

      const res = await fetch(
        `${supabaseUrl.replace(/\/+$/, "")}/functions/v1/interactive-video-comment-media-upload`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${session.access_token}`,
            apikey: supabaseAnonKey,
          },
          body: form,
        }
      );

      if (!res.ok) {
        const text = await res.text().catch(() => "");
        console.error("Upload failed:", res.status, text);
        setSaving(false);
        alert("Upload failed.");
        return;
      }

      const data = await res.json();
      setSaving(false);

      const saved = data?.comment ?? null;
      if (!saved?.id) return alert("Upload failed (no comment returned).");

      if (data?.playbackUrl) {
        setSignedMediaUrlByCommentId((m) => ({ ...m, [saved.id]: data.playbackUrl }));
      }

      setComments((prev) =>
        prev
          .map((c) => (c.id === tmpId ? saved : c))
          .sort((a, b) => (a.time_ms ?? 0) - (b.time_ms ?? 0))
      );
      setDraftById((d) => { const next = { ...d }; delete next[tmpId]; return next; });
      setDraftTypeById((t) => { const next = { ...t }; delete next[tmpId]; return next; });
      setDraftRealIdByTmpId((m) => { const next = { ...m }; delete next[tmpId]; return next; });
      cleanupDraftAudio(tmpId);
      cleanupDraftVideo(tmpId);
      setActiveCommentId(saved.id);
      scrollToComment(saved.id);
      pulseMarker(saved.id);
    } catch (e) {
      setSaving(false);
      alert("Upload failed.");
    }
  }, [comments, draftById, instanceId, pulseMarker, scrollToComment, draftTypeById, audioBlobById, audioMimeById, audioDurationMsById, videoBlobById, videoMimeById, videoDurationMsById, draftRealIdByTmpId, cleanupDraftAudio, cleanupDraftVideo]);

  const startEditComment = useCallback((c) => {
    if (!c?.id || c?.isDraft) return;
    if (c.comment_type && c.comment_type !== "text") return;
    setEditingId(c.id);
    setDraftById((d) => ({ ...d, [c.id]: c.body ?? "" }));
    setActiveCommentId(c.id);
    requestAnimationFrame(() => {
      scrollToComment(c.id);
      requestAnimationFrame(() => {
        const el = commentElByIdRef.current.get(c.id);
        const ta = el?.querySelector?.("textarea");
        if (ta) ta.focus();
      });
    });
  }, [scrollToComment]);

  const startRecording = useCallback(async (tmpId) => {
    if (!tmpId) return;
    if (isRecordingByTmpId?.[tmpId]) return;

    setDraftTypeById((t) => ({ ...t, [tmpId]: "audio" }));
    cleanupDraftAudio(tmpId);
    cleanupDraftVideo(tmpId);

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRefByTmpId.current.set(tmpId, stream);

      const mime = pickAudioMimeType() || "";
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      recorderRefByTmpId.current.set(tmpId, rec);
      chunksRefByTmpId.current.set(tmpId, []);

      rec.ondataavailable = (ev) => {
        if (!ev?.data || ev.data.size === 0) return;
        const arr = chunksRefByTmpId.current.get(tmpId) || [];
        arr.push(ev.data);
        chunksRefByTmpId.current.set(tmpId, arr);
      };

      rec.onstop = async () => {
        try {
          const parts = chunksRefByTmpId.current.get(tmpId) || [];
          const blob = new Blob(parts, { type: rec.mimeType || mime || "audio/webm" });
          setAudioBlobById((m) => ({ ...m, [tmpId]: blob }));
          const mt = blob.type || rec.mimeType || mime || "";
          setAudioMimeById((m) => ({ ...m, [tmpId]: mt }));
          const url = URL.createObjectURL(blob);
          setAudioPreviewUrlById((m) => ({ ...m, [tmpId]: url }));
          const dur = await getBlobDurationMs(blob);
          setAudioDurationMsById((m) => ({ ...m, [tmpId]: dur }));
        } catch {}
        try {
          const st = streamRefByTmpId.current.get(tmpId);
          st?.getTracks?.()?.forEach((t) => t.stop());
        } catch {}
        streamRefByTmpId.current.delete(tmpId);
        setIsRecordingByTmpId((m) => ({ ...m, [tmpId]: false }));
      };

      rec.start(250);
      setIsRecordingByTmpId((m) => ({ ...m, [tmpId]: true }));
      toast("Recording…");
    } catch (e) {
      console.error(e);
      toast("Mic permission denied.");
      setIsRecordingByTmpId((m) => ({ ...m, [tmpId]: false }));
    }
  }, [cleanupDraftAudio, isRecordingByTmpId]);

  const stopRecording = useCallback((tmpId) => {
    if (!tmpId) return;
    const rec = recorderRefByTmpId.current.get(tmpId);
    if (!rec) return;
    try { rec.stop(); } catch {}
  }, []);

  const discardRecording = useCallback((tmpId) => {
    cleanupDraftAudio(tmpId);
    toast("Recording removed.");
  }, [cleanupDraftAudio]);

  const startVideoRecording = useCallback(async (tmpId) => {
    if (!tmpId) return;
    if (isVideoRecordingByTmpId?.[tmpId]) return;

    setDraftTypeById((t) => ({ ...t, [tmpId]: "video" }));
    cleanupDraftAudio(tmpId);
    cleanupDraftVideo(tmpId);

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      videoStreamRefByTmpId.current.set(tmpId, stream);
      // Live preview while recording
      setLiveVideoStreamByTmpId((m) => ({ ...m, [tmpId]: stream }));

      const mime = pickVideoMimeType() || "";
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      videoRecorderRefByTmpId.current.set(tmpId, rec);
      videoChunksRefByTmpId.current.set(tmpId, []);

      rec.ondataavailable = (ev) => {
        if (!ev?.data || ev.data.size === 0) return;
        const arr = videoChunksRefByTmpId.current.get(tmpId) || [];
        arr.push(ev.data);
        videoChunksRefByTmpId.current.set(tmpId, arr);
      };

      rec.onstop = async () => {
        try {
          const parts = videoChunksRefByTmpId.current.get(tmpId) || [];
          const blob = new Blob(parts, { type: rec.mimeType || mime || "video/webm" });
          setVideoBlobById((m) => ({ ...m, [tmpId]: blob }));
          const mt = blob.type || rec.mimeType || mime || "";
          setVideoMimeById((m) => ({ ...m, [tmpId]: mt }));
          const url = URL.createObjectURL(blob);
          setVideoPreviewUrlById((m) => ({ ...m, [tmpId]: url }));
          const dur = await getVideoBlobDurationMs(blob);
          setVideoDurationMsById((m) => ({ ...m, [tmpId]: dur }));
        } catch {}

        try {
          const st = videoStreamRefByTmpId.current.get(tmpId);
          st?.getTracks?.()?.forEach((t) => t.stop());
        } catch {}
        videoStreamRefByTmpId.current.delete(tmpId);
        setLiveVideoStreamByTmpId((m) => {
          const next = { ...m }; delete next[tmpId]; return next;
        });
        setIsVideoRecordingByTmpId((m) => ({ ...m, [tmpId]: false }));
      };

      rec.start(250);
      setIsVideoRecordingByTmpId((m) => ({ ...m, [tmpId]: true }));
      toast("Recording video…");
    } catch (e) {
      console.error(e);
      toast("Camera/mic permission denied.");
      setIsVideoRecordingByTmpId((m) => ({ ...m, [tmpId]: false }));
    }
  }, [cleanupDraftAudio, cleanupDraftVideo, isVideoRecordingByTmpId]);

  const stopVideoRecording = useCallback((tmpId) => {
    if (!tmpId) return;
    const rec = videoRecorderRefByTmpId.current.get(tmpId);
    if (!rec) return;
    try { rec.stop(); } catch {}
  }, []);

  const discardVideoRecording = useCallback((tmpId) => {
    cleanupDraftVideo(tmpId);
    toast("Video removed.");
  }, [cleanupDraftVideo]);

  const cancelEditComment = useCallback((id) => {
    setEditingId((cur) => (cur === id ? null : cur));
    setDraftById((d) => { const next = { ...d }; delete next[id]; return next; });
  }, []);

  const saveEditedComment = useCallback(async (id) => {
    const body = (draftById?.[id] ?? "").trim();
    if (!id) return;
    if (!body) return toast("Comment cannot be empty.");

    setSaving(true);
    const { data, error } = await supabase
      .from("interactive_video_comments")
      .update({ body, comment_type: "text" })
      .eq("id", id)
      .select().single();
    setSaving(false);

    if (error) return alert(error.message);

    setComments((prev) => prev.map((c) => (c.id === id ? { ...c, ...data } : c)));
    setEditingId(null);
    setDraftById((d) => { const next = { ...d }; delete next[id]; return next; });
    pulseMarker(id);
    scrollToComment(id);
  }, [draftById, pulseMarker, scrollToComment]);

  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    const onLoaded = () => setDurationSec(Number.isFinite(el.duration) ? el.duration : 0);
    const onTime = () => setCurrentTimeSec(el.currentTime || 0);
    const onPlay = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);
    el.addEventListener("loadedmetadata", onLoaded);
    el.addEventListener("timeupdate", onTime);
    el.addEventListener("play", onPlay);
    el.addEventListener("pause", onPause);
    return () => {
      el.removeEventListener("loadedmetadata", onLoaded);
      el.removeEventListener("timeupdate", onTime);
      el.removeEventListener("play", onPlay);
      el.removeEventListener("pause", onPause);
    };
  }, [video?.public_url]);

  const formatTime = (sec) => {
    if (!Number.isFinite(sec) || sec < 0) return "0:00";
    const m = Math.floor(sec / 60);
    const s = Math.floor(sec % 60);
    return `${m}:${String(s).padStart(2, "0")}`;
  };

  const timelineMarkers = useMemo(() => (comments || []).filter((c) => typeof c?.time_ms === "number"), [comments]);

  const handleTimelineClick = (e) => {
    if (!videoRef.current || !durationSec) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = Math.min(Math.max(0, e.clientX - rect.left), rect.width);
    const ratio = rect.width ? x / rect.width : 0;
    const t = ratio * durationSec;
    seekTo(Math.floor(t * 1000), { resume: isPlaying });
  };

  const handleDelete = async () => {
    if (!canDelete) return;
    setConfirm({ kind: "tool" });
  };

  const confirmDeleteTool = async () => {
    setConfirm(null);
    setSaving(true);
    try {
      const iid = attrs.instanceId || instanceId;
      if (iid) {
        const { error } = await supabase.functions.invoke("interactive-video-instance-delete", {
          body: { instanceId: iid },
        });
        if (error) throw new Error(error.message || "Failed to delete media.");
      } else {
        // Fallback: if somehow no instanceId, at least remove DB rows
        await deleteInteractiveVideoCascade({
          instanceId: attrs.instanceId,
          lessonNoteId: attrs.lessonNoteId,
          noteBlockId: attrs.noteBlockId,
        });
      }
      const pos = typeof getPos === "function" ? getPos() : null;
      if (typeof pos === "number") {
        editor.chain().focus().deleteRange({ from: pos, to: pos + node.nodeSize }).insertContent({ type: "paragraph" }).run();
      } else {
        updateAttributes({ instanceId: null });
      }
    } catch {
      toast("Sorry—could not delete this Interactive Video tool.");
    } finally {
      setSaving(false);
    }
  };

  const requestDeleteComment = useCallback((c) => {
    if (!c?.id) return;
    if (String(c.id).startsWith("tmp_")) {
      setComments((prev) => prev.filter((x) => x.id !== c.id));
      setDraftById((d) => { const next = { ...d }; delete next[c.id]; return next; });
      setDraftTypeById((t) => { const next = { ...t }; delete next[c.id]; return next; });
      setDraftRealIdByTmpId((m) => { const next = { ...m }; delete next[c.id]; return next; });
      cleanupDraftAudio(c.id);
      cleanupDraftVideo(c.id);
      if (editingId === c.id) setEditingId(null);
      return;
    }
    setConfirm({ kind: "comment", id: c.id });
  }, [editingId, cleanupDraftAudio, cleanupDraftVideo]);

  const confirmDeleteComment = useCallback(async (id) => {
    if (!id) return;
    setConfirm(null);
    setSaving(true);
    try {
      const c = comments.find((x) => x.id === id);
      const isMedia =
        !!c &&
        (c.comment_type === "audio" || c.comment_type === "video") &&
        c.media_provider === "bunny" &&
        !!c.media_key;

      if (isMedia) {
        const { data, error } = await supabase.functions.invoke(
          "interactive-video-comment-media-delete",
          { body: { commentId: id } }
        );
        if (error || !data?.ok) {
          console.error("interactive-video-comment-media-delete failed:", error?.message, data);
          alert(error?.message || data?.error || "Failed to delete media comment.");
          return;
        }
      } else {
        const { error } = await supabase
          .from("interactive_video_comments")
          .delete()
          .eq("id", id);
        if (error) return alert(error.message);
      }

      setComments((prev) => prev.filter((c) => c.id !== id));
      setDraftById((d) => { const next = { ...d }; delete next[id]; return next; });
      setEditingId((cur) => (cur === id ? null : cur));
      setActiveCommentId((cur) => (cur === id ? null : cur));
      setSignedMediaUrlByCommentId((m) => { const next = { ...m }; delete next[id]; return next; });
      setVideoPosterByCommentId((m) => { const next = { ...m }; delete next[id]; return next; });
    } finally {
      setSaving(false);
    }
  }, [comments]);

  const showStudentMissing = audience === "students" || audience === "both";
  const showParentMissing = audience === "parents" || audience === "both";
  const missingTotal = useMemo(() => {
    let n = 0;
    if (showStudentMissing) n += (missingStudents?.length || 0);
    if (showParentMissing) n += (missingParents?.length || 0);
    return n;
  }, [showStudentMissing, showParentMissing, missingStudents, missingParents]);

  return (
    <NodeViewWrapper
       as="div"
      contentEditable={false}
      data-interactive-video-root
      className="relative group rounded-2xl bg-slate-50 shadow-sm p-4 border border-slate-200"
    >
      <div className="flex items-start justify-between gap-4 mb-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-orange-100 text-orange-600 rounded-xl flex items-center justify-center">
            <SafeIcon icon={FiVideo} className="w-6 h-6" />
          </div>
          <div>
            <div className="text-lg font-bold text-slate-800">Interactive Video</div>
            <div className="text-sm text-slate-500">Time-based feedback on a student recording</div>
          </div>
        </div>
        {canDelete && (
          <button
            className="p-2 rounded-full text-slate-500 hover:bg-red-100 hover:text-red-600 transition-colors opacity-50 group-hover:opacity-100"
            onClick={handleDelete}
            disabled={saving || loadingPreview || sendingInvite}
            title="Delete Tool"
          >
            <SafeIcon icon={FiTrash2} className="w-5 h-5" />
          </button>
        )}
      </div>

      {!video && (
        <div className="border border-dashed rounded-xl p-4 bg-white space-y-3">
          <div className="text-sm text-slate-600">Awaiting student video upload.</div>
          {isEditable && (
            <button
              onClick={() => setInviteOpen(true)}
              className="inline-flex items-center gap-2 px-3 py-2 bg-orange-500 text-white rounded-lg text-sm hover:bg-orange-600"
            >
              <SafeIcon icon={FiSave} /> Invite student to upload video
            </button>
          )}
        </div>
      )}

      {video && (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 items-stretch min-h-0 lg:h-[70vh] lg:max-h-[820px]">
          <div className="lg:col-span-8 flex flex-col min-h-0 h-full">
            <div className="bg-white rounded-2xl p-3 shadow-lg ring-1 ring-black/5 flex flex-col min-h-0 h-full">
              <div className="w-full rounded-xl bg-black overflow-hidden flex items-center justify-center flex-1 min-h-0">
                <video
                  ref={videoRef}
                  controls
                  src={video.public_url}
                  className="max-h-full max-w-full w-auto h-auto"
                />
              </div>
              <div className="mt-3">
                <div className="flex items-center justify-between text-[11px] text-slate-400 mb-1">
                  <span>{formatTime(currentTimeSec)}</span>
                  <span>{formatTime(durationSec)}</span>
                </div>
                <div
                  className="relative h-4 rounded-full bg-slate-100 border border-slate-200 cursor-pointer select-none"
                  onClick={handleTimelineClick}
                >
                  <div className="absolute inset-y-0 left-0 rounded-full bg-slate-200" style={{ width: durationSec ? `${Math.min(100, (currentTimeSec / durationSec) * 100)}%` : "0%" }} />
                  {timelineMarkers.map((c, idx) => {
                    const color = colorFor(c, idx);
                    const tSec = (c.time_ms || 0) / 1000;
                    const leftPct = durationSec ? Math.min(100, Math.max(0, (tSec / durationSec) * 100)) : 0;
                    return (
                      <button
                        key={c.id}
                        type="button"
                        onClick={(e) => { e.stopPropagation(); handleMarkerClick(c); }}
                        className={`absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-4 h-4 rounded-full border bg-white shadow-sm transition-transform duration-150 ${activeCommentId === c.id ? `ring-2 ${color.ring} scale-110` : "hover:scale-110"} ${flashMarkerId === c.id ? "animate-pulse" : ""}`}
                        style={{ left: `${leftPct}%` }}
                      >
                        <span className={`block w-full h-full rounded-full ${color.dot}`} />
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
            {isEditable && (
              <div className="mt-4 flex items-center justify-center">
                <button
                  type="button"
                  onClick={addDraftCommentAtCurrentTime}
                  disabled={saving}
                  className="bg-gradient-to-r from-orange-400 to-red-400 text-white px-5 py-2.5 rounded-xl font-semibold hover:from-orange-500 hover:to-red-500 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2 shadow-lg shadow-orange-200/60"
                >
                  {saving ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  ) : (
                    <SafeIcon icon={FiPlus} className="h-4 w-4" />
                  )}
                  <span>{saving ? "Saving..." : "Add Comment"}</span>
                </button>
              </div>
            )}
          </div>

          <div className="lg:col-span-4 min-h-0 h-full">
            <div className="bg-white rounded-2xl shadow-lg ring-1 ring-black/5 h-full min-h-0 flex flex-col overflow-hidden">
              <div className="px-4 py-3 border-b border-slate-100/70">
                <div className="text-lg font-semibold text-slate-900">Comments</div>
                <div className="mt-2 h-1 rounded-full bg-gradient-to-r from-orange-400 via-pink-500 to-fuchsia-500" />
              </div>
              <div ref={commentsPaneRef} className="flex-1 min-h-0 overflow-auto p-3 space-y-3">
                {comments.length === 0 ? (
                  <div className="text-sm text-slate-500 px-2 py-6 text-center">No comments yet.</div>
                ) : (
                  comments.map((c, idx) => {
                    const color = colorFor(c, idx);
                    const isActive = activeCommentId === c.id;
                    const isDraft = !!c.isDraft;
                    const isEditing = editingId === c.id;
                    const isMedia = (c.comment_type === "audio" || c.comment_type === "video");
                    return (
                      <div
                        key={c.id}
                        ref={(el) => { if (el) commentElByIdRef.current.set(c.id, el); else commentElByIdRef.current.delete(c.id); }}
                        onClick={() => {
                          handleCardClick(c);
                        }}
                        className={[
                          "relative rounded-2xl border p-3 shadow-sm transition-transform duration-200 cursor-pointer",
                          "group/comment",
                          color.chip,
                          isActive ? "ring-2 ring-slate-300" : "",
                          flashCommentId === c.id ? "scale-[1.02]" : "",
                        ].join(" ")}
                      >
                        <div className="flex items-start justify-between gap-3 mb-2">
                          <div className="flex items-center gap-2 text-left min-w-0 pointer-events-none">
                            <div className="w-7 h-7 rounded-full bg-white/70 border border-white flex items-center justify-center text-xs font-bold">{(idx + 1).toString()}</div>
                            <div className="text-sm font-semibold">{formatTime((c.time_ms || 0) / 1000)}</div>
                          </div>
                        </div>

                        {isEditable && !isDraft && !isEditing && (c.comment_type ?? "text") === "text" && (
                          <div className="absolute top-3 right-3 flex items-center gap-2 opacity-0 group-hover/comment:opacity-100 transition-opacity">
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); startEditComment(c); }}
                              className="p-1.5 rounded-xl bg-white/60 hover:bg-white/80 border border-white/70 text-slate-700"
                              title="Edit comment"
                            >
                              <SafeIcon icon={FiEdit2} className="w-4 h-4" />
                            </button>
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); requestDeleteComment(c); }}
                              className="p-1.5 rounded-xl bg-white/60 hover:bg-white/80 border border-white/70 text-slate-700"
                              title="Delete comment"
                            >
                              <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                            </button>
                          </div>
                        )}

                        {isEditable && !isDraft && !isEditing && isMedia && (
                          <div className="absolute top-3 right-3 flex items-center gap-2 opacity-0 group-hover/comment:opacity-100 transition-opacity">
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); requestDeleteComment(c); }}
                              className="p-1.5 rounded-xl bg-white/60 hover:bg-white/80 border border-white/70 text-slate-700"
                              title="Delete comment"
                            >
                              <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                            </button>
                          </div>
                        )}

                        {isEditable && (isDraft || isEditing) ? (
                          <div onClick={(e) => e.stopPropagation()}>
                            {isDraft && (
                              <div className="mb-2 flex items-center justify-between gap-2">
                                <div className="flex items-center gap-2">
                                  <button
                                    type="button"
                                    className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${
                                      (draftTypeById?.[c.id] ?? "text") === "text"
                                        ? "bg-white text-slate-800 border-white/70"
                                        : "bg-white/40 text-slate-700 border-white/70 hover:bg-white/60"
                                    }`}
                                    onClick={() => {
                                      setDraftTypeById((t) => ({ ...t, [c.id]: "text" }));
                                    }}
                                  >
                                    Text
                                  </button>
                                  <button
                                    type="button"
                                    className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${
                                      (draftTypeById?.[c.id] ?? "text") === "audio"
                                        ? "bg-white text-slate-800 border-white/70"
                                        : "bg-white/40 text-slate-700 border-white/70 hover:bg-white/60"
                                    }`}
                                    onClick={() => setDraftTypeById((t) => ({ ...t, [c.id]: "audio" }))}
                                  >
                                    🎙 Voice note
                                  </button>
                                  <button
                                    type="button"
                                    className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${
                                      (draftTypeById?.[c.id] ?? "text") === "video"
                                        ? "bg-white text-slate-800 border-white/70"
                                        : "bg-white/40 text-slate-700 border-white/70 hover:bg-white/60"
                                    }`}
                                    onClick={() => setDraftTypeById((t) => ({ ...t, [c.id]: "video" }))}
                                  >
                                    🎥 Video note
                                  </button>
                                </div>
                              </div>
                            )}

                            {(isEditing || (isDraft && (draftTypeById?.[c.id] ?? "text") === "text")) && (
                              <textarea
                                className="w-full bg-white/70 border border-white rounded-xl p-2 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-white"
                                placeholder="Type your comment… (Enter to save • Shift+Enter for a new line)"
                                value={draftById?.[c.id] ?? (isEditing ? c.body ?? "" : "")}
                                onChange={(e) => setDraftById((d) => ({ ...d, [c.id]: e.target.value }))}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter" && !e.shiftKey) {
                                    e.preventDefault();
                                    if (isDraft) saveDraftComment(c.id);
                                    else saveEditedComment(c.id);
                                  }
                                }}
                              />
                            )}

                            {isDraft && (draftTypeById?.[c.id] ?? "text") === "audio" && (
                              <div className="bg-white/60 border border-white/70 rounded-xl p-2">
                                <div className="flex items-center justify-between gap-2">
                                  <div className="text-xs font-semibold text-slate-700 flex items-center gap-2">
                                    <SafeIcon icon={FiMic} className="w-4 h-4" />
                                    <span>Voice note</span>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    {!isRecordingByTmpId?.[c.id] ? (
                                      <button
                                        type="button"
                                        onClick={() => startRecording(c.id)}
                                        className="px-3 py-1.5 rounded-full bg-white text-slate-800 text-xs font-semibold border border-white/70 hover:bg-white/90 inline-flex items-center gap-1"
                                      >
                                        <SafeIcon icon={FiPlay} className="w-3.5 h-3.5" />
                                        <span>Record</span>
                                      </button>
                                    ) : (
                                      <button
                                        type="button"
                                        onClick={() => stopRecording(c.id)}
                                        className="px-3 py-1.5 rounded-full bg-red-500 text-white text-xs font-semibold hover:bg-red-600 inline-flex items-center gap-1"
                                      >
                                        <SafeIcon icon={FiSquare} className="w-3.5 h-3.5" />
                                        <span>Stop</span>
                                      </button>
                                    )}
                                    {!!audioPreviewUrlById?.[c.id] && (
                                      <button
                                        type="button"
                                        onClick={() => discardRecording(c.id)}
                                        className="px-3 py-1.5 rounded-full bg-white/70 text-slate-700 text-xs font-semibold border border-white/70 hover:bg-white/90 inline-flex items-center gap-1"
                                        title="Discard"
                                      >
                                        <SafeIcon icon={FiRotateCcw} className="w-3.5 h-3.5" />
                                        <span>Discard</span>
                                      </button>
                                    )}
                                  </div>
                                </div>

                                {!!audioPreviewUrlById?.[c.id] && (
                                  <div className="mt-2 text-center">
                                    <audio controls className="w-full h-10" src={audioPreviewUrlById[c.id]} />
                                    <div className="mt-1 text-[11px] text-slate-500">
                                      {audioDurationMsById?.[c.id] != null
                                        ? `Duration: ${Math.max(0, Math.round((audioDurationMsById[c.id] || 0) / 1000))}s`
                                        : "Ready to save"}
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}

                            {isDraft && (draftTypeById?.[c.id] ?? "text") === "video" && (
                              <div className="bg-white/60 border border-white/70 rounded-xl p-2">
                                <div className="flex items-center justify-between gap-2">
                                  <div className="text-xs font-semibold text-slate-700 flex items-center gap-2">
                                    <SafeIcon icon={FiCamera} className="w-4 h-4" />
                                    <span>Video note</span>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    {!isVideoRecordingByTmpId?.[c.id] ? (
                                      <button
                                        type="button"
                                        onClick={() => startVideoRecording(c.id)}
                                        className="px-3 py-1.5 rounded-full bg-white text-slate-800 text-xs font-semibold border border-white/70 hover:bg-white/90 inline-flex items-center gap-1"
                                      >
                                        <SafeIcon icon={FiPlay} className="w-3.5 h-3.5" />
                                        <span>Record</span>
                                      </button>
                                    ) : (
                                      <button
                                        type="button"
                                        onClick={() => stopVideoRecording(c.id)}
                                        className="px-3 py-1.5 rounded-full bg-red-500 text-white text-xs font-semibold hover:bg-red-600 inline-flex items-center gap-1"
                                      >
                                        <SafeIcon icon={FiSquare} className="w-3.5 h-3.5" />
                                        <span>Stop</span>
                                      </button>
                                    )}
                                    {!!videoPreviewUrlById?.[c.id] && (
                                      <button
                                        type="button"
                                        onClick={() => discardVideoRecording(c.id)}
                                        className="px-3 py-1.5 rounded-full bg-white/70 text-slate-700 text-xs font-semibold border border-white/70 hover:bg-white/90 inline-flex items-center gap-1"
                                        title="Discard"
                                      >
                                        <SafeIcon icon={FiRotateCcw} className="w-3.5 h-3.5" />
                                        <span>Discard</span>
                                      </button>
                                    )}
                                  </div>
                                </div>

                                {!!isVideoRecordingByTmpId?.[c.id] && !!liveVideoStreamByTmpId?.[c.id] && (
                                  <div className="mt-2">
                                    <LiveStreamVideo
                                      stream={liveVideoStreamByTmpId[c.id]}
                                      className="w-full rounded-lg bg-black max-h-[240px] object-contain"
                                    />
                                    <div className="mt-1 text-[11px] text-slate-500 text-center">
                                      Recording…
                                    </div>
                                  </div>
                                )}

                                {!!videoPreviewUrlById?.[c.id] && (
                                  <div className="mt-2 text-center">
                                    <video controls className="w-full rounded-lg bg-black max-h-[240px] object-contain" src={videoPreviewUrlById[c.id]} />
                                    <div className="mt-1 text-[11px] text-slate-500">
                                      {videoDurationMsById?.[c.id] != null
                                        ? `Duration: ${Math.max(0, Math.round((videoDurationMsById[c.id] || 0) / 1000))}s`
                                        : "Ready to save"}
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="text-sm text-slate-800 whitespace-pre-wrap">
                            {c.comment_type === "audio" ? (
                              <div>
                                <audio
                                  controls
                                  className="w-full h-10"
                                  src={signedMediaUrlByCommentId?.[c.id] || ""}
                                />
                                {!signedMediaUrlByCommentId?.[c.id] && (
                                  <div className="mt-1 text-[11px] text-slate-500">Loading audio…</div>
                                )}
                              </div>
                            ) : c.comment_type === "video" ? (
                              <div>
                                <video
                                  controls
                                  className="w-full rounded-lg bg-black"
                                  src={signedMediaUrlByCommentId?.[c.id] || ""}
                                  poster={
                                    videoPosterByCommentId?.[c.id]
                                      ? videoPosterByCommentId[c.id]
                                      : undefined
                                  }
                                  preload="metadata"
                                />
                                {!signedMediaUrlByCommentId?.[c.id] && (
                                  <div className="mt-1 text-[11px] text-slate-500">Loading video…</div>
                                )}
                              </div>
                            ) : (
                              c.body
                            )}
                          </div>
                        )}
                        {isEditable && (isDraft || isEditing) && (
                            <div
                                className="mt-2 flex items-center justify-end gap-2"
                                onPointerDown={(e) => e.stopPropagation()} // extra safety: prevents card click + PM selection (mouse/touch/pen)
                                onClick={(e) => e.stopPropagation()}
                            >
                                {isEditing && (
                                <button
                                    type="button"
                                    onPointerDown={(e) => {
                                    e.stopPropagation();
                                    }}
                                    onClick={(e) => {
                                    e.stopPropagation();
                                    cancelEditComment(c.id);
                                    }}
                                    className="px-3 py-1.5 rounded-full bg-white/60 text-slate-700 text-xs font-semibold border border-white/70 hover:bg-white/80"
                                >
                                    Cancel
                                </button>
                                )}
                                <button
                                type="button"
                                onPointerDown={(e) => {
                                    e.stopPropagation();
                                }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    if (isDraft) saveDraftComment(c.id);
                                    else saveEditedComment(c.id);
                                }}
                                className="px-3 py-1.5 rounded-full bg-white text-slate-800 text-xs font-semibold border border-white/70 hover:bg-white/90"
                                >
                                Save
                                </button>
                            </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {confirm?.kind === "tool" && (
        <ConfirmModal
          title="Delete Interactive Video tool?"
          message="This will delete the Interactive Video tool (including comments, uploads, and tokens). This cannot be undone."
          confirmText="Delete"
          confirmButtonClass="bg-red-500 hover:bg-red-600"
          confirmDisabled={saving}
          onConfirm={confirmDeleteTool}
          onClose={() => setConfirm(null)}
        />
      )}
      {confirm?.kind === "comment" && (
        <ConfirmModal
          title="Delete comment?"
          message={
            (() => {
                const c = comments.find((x) => x.id === confirm?.id);
                const isMedia = c && (c.comment_type === "audio" || c.comment_type === "video") && c.media_provider === "bunny" && c.media_key;
                return isMedia
                ? "This will permanently delete this comment and its audio/video file. This cannot be undone."
                : "This will permanently delete this comment.";
            })()
          }
          confirmText="Delete"
          confirmButtonClass="bg-red-500 hover:bg-red-600"
          confirmDisabled={saving}
          onConfirm={() => confirmDeleteComment(confirm?.id)}
          onClose={() => setConfirm(null)}
        />
      )}

      {inviteOpen && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4" onClick={() => setInviteOpen(false)}>
          <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full overflow-hidden flex flex-col max-h-[90vh]" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 z-10 bg-white border-b border-slate-100 px-6 py-4">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <h2 className="text-lg font-semibold text-slate-900">Invite to upload video</h2>
                  <p className="text-sm text-slate-600 truncate">Send an upload link to parents and/or the student.</p>
                  {typeof sentCount === "number" && <p className="text-xs text-green-600 mt-1">Sent {sentCount} email{sentCount === 1 ? "" : "s"}.</p>}
                </div>
                <button type="button" onClick={() => setInviteOpen(false)} className="p-1 rounded-full hover:bg-slate-100 text-slate-500"><SafeIcon icon={FiX} className="w-5 h-5" /></button>
              </div>
            </div>
            <div className="flex-1 overflow-auto px-6 py-4">
              <div className="mb-4">
                <label className="block text-xs font-semibold text-slate-500 mb-1">Audience</label>
                <div className="flex flex-wrap gap-2">
                  {AUDIENCE_OPTIONS.map((opt) => (
                    <button key={opt.value} type="button" onClick={() => setAudience(opt.value)} className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs border ${audience === opt.value ? "bg-orange-50 border-orange-400 text-orange-700" : "bg-white border-slate-200 text-slate-600 hover:border-slate-300"}`} disabled={loadingPreview || sendingInvite}>
                      <SafeIcon icon={FiUsers} className="w-3 h-3" /><span>{opt.label}</span>
                    </button>
                  ))}
                </div>
              </div>
              <div className="border rounded-xl border-slate-200 max-h-64 overflow-auto mb-4">
                <div className="flex items-center justify-between px-3 py-2 border-b border-slate-100 bg-slate-50">
                  <div className="flex items-center gap-2 text-xs font-semibold text-slate-600"><SafeIcon icon={FiMail} className="w-3 h-3" /><span>Recipients</span></div>
                  {templateId && <span className="text-[11px] text-slate-400">Brevo template #{templateId}</span>}
                </div>
                {loadingPreview ? <div className="px-3 py-4 text-sm text-slate-500">Loading recipients…</div> : recipients.length === 0 ? <div className="px-3 py-4 text-sm text-slate-500">No recipients found for this audience.</div> : (
                  <ul className="divide-y divide-slate-100 text-sm">
                    {recipients.map((r) => (
                      <li key={r.id} className="flex items-center gap-3 px-3 py-2">
                        <input type="checkbox" className="rounded border-slate-300" checked={!!r.selected} onChange={() => toggleRecipient(r.id)} disabled={sendingInvite} />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-slate-900 truncate">{r.name || r.email}</span>
                            <span className="text-[11px] uppercase tracking-wide text-slate-400">{r.type}</span>
                          </div>
                          <div className="text-xs text-slate-500 truncate">{r.email}</div>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              {missingTotal > 0 && (
                <div className="mb-3 p-3 rounded-xl bg-yellow-50 border border-yellow-200 text-sm text-yellow-700">
                  <div className="font-semibold mb-1">Missing contact information</div>
                  {showStudentMissing && (missingStudents?.length || 0) > 0 && (
                    <div className="mb-2"><div className="font-medium">Students missing email:</div><ul className="list-disc ml-5">{missingStudents.map((s, idx) => <li key={idx}>{s.name || "Student"}</li>)}</ul></div>
                  )}
                  {showParentMissing && (missingParents?.length || 0) > 0 && (
                    <div><div className="font-medium">Parents missing email:</div><ul className="list-disc ml-5">{missingParents.map((p, idx) => <li key={idx}>{p.name || "Parent"}</li>)}</ul></div>
                  )}
                </div>
              )}
              <div className="mb-3 flex items-center gap-2 text-sm text-slate-700">
                <input id="notify-on-upload" type="checkbox" className="rounded border-slate-300" checked={!!notifyOnUpload} onChange={(e) => setNotifyOnUpload(e.target.checked)} disabled={sendingInvite} />
                <label htmlFor="notify-on-upload" className="cursor-pointer">Notify me when the video is uploaded {teacherEmail && <>at <span className="font-medium">{teacherEmail}</span></>}</label>
              </div>
              <div className="mb-3 flex items-center gap-2 text-sm text-slate-700">
                <input id="cc-teacher" type="checkbox" className="rounded border-slate-300" checked={!!ccTeacher} onChange={(e) => setCcTeacher(e.target.checked)} disabled={sendingInvite} />
                <label htmlFor="cc-teacher" className="cursor-pointer">Send me a copy of the invite email {teacherEmail && <>at <span className="font-medium">{teacherEmail}</span></>}</label>
              </div>
              {inviteError && <div className="mb-3 text-sm text-red-600">{inviteError}</div>}
            </div>
            <div className="sticky bottom-0 bg-white border-t border-slate-100 px-6 py-4 flex items-center justify-between gap-3">
              <button type="button" onClick={() => setInviteOpen(false)} className="text-sm text-slate-600 hover:text-slate-900" disabled={sendingInvite}>Close</button>
              <button type="button" onClick={handleSendInvite} disabled={sendingInvite || loadingPreview || recipients.length === 0} className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium ${sendingInvite || loadingPreview || recipients.length === 0 ? "bg-slate-200 text-slate-500 cursor-not-allowed" : "bg-orange-600 text-white hover:bg-orange-700"}`}>
                <SafeIcon icon={FiSend} className="w-4 h-4" /><span>{sendingInvite ? "Sending…" : "Send emails"}</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </NodeViewWrapper>
  );
}

const InteractiveVideoNode = Node.create({
  name: "interactiveVideo",
  group: "block",
  draggable: false,
  // Critical: do NOT allow NodeSelection on this tool.
  // This prevents "typing replaces node" and removes the need for event-swallowing hacks.
  selectable: false,
  addAttributes() {
    return {
      noteBlockId: { default: null },
      lessonNoteId: { default: null },
      schoolId: { default: null },
      studentId: { default: null },
      studentMasterId: { default: null },
      instanceId: { default: null },
    };
  },
  parseHTML() { return [{ tag: "interactive-video" }]; },
  renderHTML({ HTMLAttributes }) { return ["interactive-video", mergeAttributes(HTMLAttributes)]; },
  addNodeView() {
    return ReactNodeViewRenderer(InteractiveVideoBlock);
  },
  addProseMirrorPlugins() {
  const type = this.type;

  const includesInteractiveVideo = (doc, from, to, nodeType) => {
    let includesAny = false;
    let includesSaved = false;
    doc.nodesBetween(from, to, (n) => {
      if (n.type === nodeType) {
        includesAny = true;
        if (n.attrs?.instanceId) includesSaved = true;
      }
      return true;
    });
    return { includesAny, includesSaved };
  };

  return [
    new Plugin({
      props: {
        handleKeyDown(view, event) {
          const sel = view.state.selection;

          // 0) Prevent Backspace/Delete from removing the node when the cursor is adjacent to it.
          // This enforces: "only delete via the trash icon".
          if (event.key === "Backspace" || event.key === "Delete") {
            if (sel.from === sel.to) {
              const $from = sel.$from;
              const nodeBefore = $from.nodeBefore;
              const nodeAfter = $from.nodeAfter;

              // Backspace deletes nodeBefore, Delete deletes nodeAfter in typical PM behavior.
              const hit =
                (event.key === "Backspace" && nodeBefore?.type === type && nodeBefore?.attrs?.instanceId) ||
                (event.key === "Delete" && nodeAfter?.type === type && nodeAfter?.attrs?.instanceId);

              if (hit) {
                if (view?.editable) toast("Use the Delete button to remove the Interactive Video tool.");
                event.preventDefault();
                return true;
              }
            }
          }

          // 1) If THIS node is selected, prevent ProseMirror from replacing it with typed text.
          if (sel instanceof NodeSelection && sel.node?.type === type) {
            // Keep your Backspace/Delete guard behavior
            if (event.key === "Backspace" || event.key === "Delete") {
              if (sel.node.attrs?.instanceId) {
                if (view?.editable) toast("Use the Delete button to remove the Interactive Video tool.");
                event.preventDefault();
                return true;
              }
              return false;
            }

            // Block keys that would replace the selected node (letters, numbers, Enter, Space, etc.)
            // Allow navigation + modifier shortcuts.
            const k = event.key;

            const isNavKey =
              k === "ArrowLeft" || k === "ArrowRight" || k === "ArrowUp" || k === "ArrowDown" ||
              k === "Home" || k === "End" || k === "PageUp" || k === "PageDown" ||
              k === "Tab" || k === "Escape";

            const isModifierCombo = event.metaKey || event.ctrlKey || event.altKey;

            if (!isNavKey && !isModifierCombo) {
              event.preventDefault();
              return true;
            }

            return false;
          }

          // 2) Your existing selection-range delete protection (Backspace/Delete over a range)
          if (event.key === "Backspace" || event.key === "Delete") {
            if (sel.from !== sel.to) {
              const { includesAny, includesSaved } = includesInteractiveVideo(view.state.doc, sel.from, sel.to, type);
              if (includesAny) {
                if (includesSaved && view?.editable) toast("Use the Delete button to remove the Interactive Video tool.");
                event.preventDefault();
                return true;
              }
            }
          }

          return false;
        },

        handleTextInput(view, from, to, text) {
          // This is the direct codepath ProseMirror uses to replace a NodeSelection with typed text.
          const sel = view.state.selection;
          if (sel instanceof NodeSelection && sel.node?.type === type) {
            return true; // swallow text insertion, keep node
          }
          return false;
        },

        handleDOMEvents: {
          cut(view, event) {
            const sel = view.state.selection;
            if (sel.from !== sel.to) {
              const { includesAny, includesSaved } = includesInteractiveVideo(view.state.doc, sel.from, sel.to, type);
              if (includesAny && includesSaved) {
                toast("Use the Delete button to remove the Interactive Video tool.");
                event.preventDefault();
                return true;
              }
            }
            return false;
          },
        },
      },
    }),
  ];
},
  addKeyboardShortcuts() {
    const handleKey = ({ editor }) => {
      const sel = editor?.state?.selection;
      if (!(sel instanceof NodeSelection) || sel.node?.type?.name !== this.name) return false;
      if (sel.node.attrs?.instanceId) {
        if (editor?.isEditable) toast("Use the Delete button to remove this Interactive Video tool.");
        return true;
      }
      return false;
    };
    return { Backspace: handleKey, Delete: handleKey };
  },
});

export default InteractiveVideoNode;